import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { authApi } from "@/api/services";
import type { SocialProvidersResponse } from "@/api/types";
import { setAccessToken, setRefreshToken } from "@/api/client";
import { useAuthStore, displayNameOf } from "@/stores/auth";
import { validatePassword, PASSWORD_HINT } from "@/lib/passwordPolicy";

// ---------------------------------------------------------------------------
// Social sign-in SVG icons
// ---------------------------------------------------------------------------

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

interface FormErrors {
  display_name?: string;
  email?: string;
  password?: string;
  confirm_password?: string;
  terms?: string;
}

function validateForm(fields: {
  display_name: string;
  email: string;
  password: string;
  confirm_password: string;
}): FormErrors {
  const errors: FormErrors = {};

  if (!fields.display_name.trim()) {
    errors.display_name = "Display name is required.";
  }

  if (!fields.email.trim()) {
    errors.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
    errors.email = "Please enter a valid email address.";
  }

  const passwordError = validatePassword(fields.password);
  if (passwordError) {
    errors.password = passwordError;
  }

  if (!fields.confirm_password) {
    errors.confirm_password = "Please confirm your password.";
  } else if (fields.password !== fields.confirm_password) {
    errors.confirm_password = "Passwords do not match.";
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Register Page
// ---------------------------------------------------------------------------

/** Title-cases a plan_code for display. Deliberately NOT a lookup table: the
 *  four active codes (STARTER/PROFESSIONAL/BUSINESS/ENTERPRISE) are words, and
 *  a code with no entry must still render as itself rather than as a blank. */
function planLabel(code: string): string {
  return code.charAt(0) + code.slice(1).toLowerCase();
}

function Register() {
  const navigate = useNavigate();
  // Every pricing card links here as /register?plan=<plan_code> (Pricing.tsx:93,
  // pinned by Pricing.test.tsx). Nothing read it: the choice was discarded and
  // signup hard-codes STARTER (auth.py:303). All four active plans are paid
  // ($29-$299), so this was every paying arrival, not an edge case.
  const [searchParams] = useSearchParams();
  const chosenPlan = (searchParams.get("plan") ?? "").trim().toUpperCase();
  // `/billing` is the only surface that can act on it, and it must be a
  // deliberate click there — never an automatic charge on arrival.
  const billingHref = chosenPlan ? `/billing?plan=${encodeURIComponent(chosenPlan)}` : "/";
  const setUser = useAuthStore((s) => s.setUser);

  // Form fields
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState("");
  // Signup no longer hands out a session to an unverified address: the account is
  // created, but the address must be proven first. When set, we show "check your
  // inbox" instead of navigating into an app the user cannot yet use.
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState("");

  // Social providers configuration (fetched from backend)
  const [socialProviders, setSocialProviders] = useState<SocialProvidersResponse | null>(null);

  useEffect(() => {
    authApi.socialProviders().then(setSocialProviders).catch((err) => {
      console.warn("Failed to fetch social providers from API:", err);
      // Fallback: check build-time env vars so buttons still show
      const viteGoogleId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      const viteAppleId = import.meta.env.VITE_APPLE_CLIENT_ID;
      if (viteGoogleId || viteAppleId) {
        setSocialProviders({
          google: { enabled: !!viteGoogleId, client_id: viteGoogleId || null },
          apple: { enabled: !!viteAppleId },
        });
      }
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setApiError("");

    const validationErrors = validateForm({
      display_name: displayName,
      email,
      password,
      confirm_password: confirmPassword,
    });

    if (!acceptTerms) {
      validationErrors.terms = "Please accept the Terms and Privacy Policy to continue.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const res = await authApi.register({
        email: email.trim(),
        password,
        display_name: displayName.trim(),
        org_name: orgName.trim() || undefined,
        invite_code: inviteCode.trim() || undefined,
        // The checkbox is validated above; send it so the SERVER records the acceptance
        // (version + timestamp + IP). It also enforces it, so a bypassed UI cannot skip it.
        accept_terms: acceptTerms,
        // ROW f8a46a1b. Send the plan chosen on /pricing so the SERVER records
        // it. Verification is on in production, so this call returns
        // verification_required and the navigate() below never runs — the
        // customer leaves to read their email, often on another device, and
        // every client-side way of carrying the choice dies there.
        plan: chosenPlan || undefined,
      });

      // Without this branch the SPA would set an undefined token and immediately 401
      // on /me — signup would LOOK broken to the user even though it worked.
      if (res.verification_required) {
        setPendingVerificationEmail(res.email ?? email.trim());
        setLoading(false);
        return;
      }

      setAccessToken(res.access_token);
      setRefreshToken(res.refresh_token);

      // Fetch the authenticated user profile and update the store
      const me = await authApi.me();
      setUser({
        id: me.id,
        email: email.trim(),
        name: displayName.trim(),
        roles: me.roles ?? [],
        org_id: me.org_id,
        org_name: orgName.trim() || "",
      });

      navigate(billingHref);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Registration failed. Please try again.";
      setApiError(message);
    } finally {
      setLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Social sign-in handler
  // ---------------------------------------------------------------------------

  const handleSocialSignup = useCallback(
    async (provider: "google" | "apple") => {
      setApiError("");
      setSocialLoading(provider);

      try {
        let idToken: string | null = null;

        const googleSdk = window.google?.accounts?.id;
        const googleClientId = socialProviders?.google?.client_id
          || import.meta.env.VITE_GOOGLE_CLIENT_ID
          || "";

        if (provider === "google" && googleSdk) {
          if (!googleClientId) {
            throw new Error("Google sign-in is not configured. Please contact your administrator.");
          }
          idToken = await new Promise<string>((resolve, reject) => {
            googleSdk.initialize({
              client_id: googleClientId,
              callback: (response: { credential: string }) => {
                resolve(response.credential);
              },
            });
            googleSdk.prompt((notification) => {
              if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                reject(new Error("Google sign-in was cancelled. Please try again or use email/password."));
              }
            });
          });
        } else if (provider === "apple" && window.AppleID) {
          const appleResponse = await window.AppleID.auth.signIn();
          idToken = appleResponse.authorization.id_token;
        } else {
          throw new Error(
            `${provider === "google" ? "Google" : "Apple"} sign-in is not available. Please ensure the SDK is loaded.`
          );
        }

        if (!idToken) {
          throw new Error("Failed to obtain ID token");
        }

        const { access_token, refresh_token } = await authApi.socialLogin({
          id_token: idToken,
          provider,
        });

        setAccessToken(access_token);
        setRefreshToken(refresh_token);

        const me = await authApi.me();
        const ext = me as unknown as { display_name?: string; email?: string };
        setUser({
          id: me.id,
          name: displayNameOf(ext),
          email: ext.email || "",
          roles: me.roles ?? [],
          org_id: me.org_id,
          org_name: "",
        });

        navigate("/");
      } catch (err: unknown) {
        if (err instanceof Error) {
          setApiError(err.message);
        } else {
          setApiError("Social sign-up failed. Please try again.");
        }
      } finally {
        setSocialLoading(null);
      }
    },
    [navigate, setUser],
  );

  // ---------------------------------------------------------------------------
  // Password visibility toggle button
  // ---------------------------------------------------------------------------

  function ToggleButton({
    visible,
    onToggle,
  }: {
    visible: boolean;
    onToggle: () => void;
  }) {
    return (
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
        onClick={onToggle}
        tabIndex={-1}
        aria-label={visible ? "Hide password" : "Show password"}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // The account exists, but the address has not been proven yet, so there is no session
  // to navigate into. Say so plainly — a silent no-op here reads as "signup is broken".
  if (pendingVerificationEmail) {
    return (
      <AuthShell
        eyebrow="One more step"
        title="Confirm your email"
        subtitle="Your account is created. We just need to know the address is yours."
        illustrationTagline="From objective to production — every step traced, every decision captured."
        footer={
          <>
            Already confirmed?{" "}
            <Link
              to="/login"
              className="font-semibold text-cyan-600 dark:text-cyan-400 hover:underline"
            >
              Sign in
            </Link>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            We sent a confirmation link to{" "}
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {pendingVerificationEmail}
            </span>
            . Open it to finish setting up your account.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            The link expires in 24 hours. If it does not arrive within a few minutes,
            check your spam folder.
          </p>
          {chosenPlan && (
            <p data-testid="pending-plan" className="text-sm text-slate-600 dark:text-slate-300">
              We noted that you chose the{" "}
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                {planLabel(chosenPlan)}
              </span>{" "}
              plan. Nothing has been charged — once you have confirmed your
              address and signed in, pick it up on{" "}
              <Link
                to={billingHref}
                className="font-semibold text-cyan-600 dark:text-cyan-400 hover:underline"
              >
                your billing page
              </Link>
              .
            </p>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Get started"
      title="Create your FTL account"
      subtitle="Spin up your organization and let Felix orchestrate your first project within minutes."
      illustrationTagline="From objective to production — every step traced, every decision captured."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-brand-600 hover:text-brand-700 transition-colors"
          >
            Sign in
          </Link>
        </>
      }
    >
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* The plan the user picked on /pricing. Shown because signing up
                does NOT subscribe them to it — the account is created on the
                starter tier and the choice is carried to /billing. Saying so
                here is the difference between a carried choice and a lost one. */}
            {chosenPlan && (
              <p
                data-testid="chosen-plan"
                className="rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-800 dark:bg-brand-900 dark:text-brand-200"
              >
                You picked the <span className="font-semibold">{planLabel(chosenPlan)}</span>{" "}
                plan. Create your account first — we will take you to checkout next,
                and nothing is charged until you confirm it there.
              </p>
            )}

            {/* API error */}
            {apiError && (
              <p className="rounded-md bg-error/10 px-3 py-2 text-sm text-error">
                {apiError}
              </p>
            )}

            {/* Display Name */}
            <div className="space-y-1.5">
              <Label htmlFor="display_name" required>
                Display Name
              </Label>
              <Input
                id="display_name"
                type="text"
                placeholder="Jane Doe"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                variant={errors.display_name ? "error" : "default"}
                autoComplete="name"
                disabled={loading}
              />
              {errors.display_name && (
                <p className="text-xs text-error">{errors.display_name}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email" required>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant={errors.email ? "error" : "default"}
                autoComplete="email"
                disabled={loading}
              />
              {errors.email && (
                <p className="text-xs text-error">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="password" required>
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 12 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant={errors.password ? "error" : "default"}
                  autoComplete="new-password"
                  className="pr-10"
                  disabled={loading}
                />
                <ToggleButton
                  visible={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              </div>
              {errors.password ? (
                <p className="text-xs text-error">{errors.password}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{PASSWORD_HINT}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label htmlFor="confirm_password" required>
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant={errors.confirm_password ? "error" : "default"}
                  autoComplete="new-password"
                  className="pr-10"
                  disabled={loading}
                />
                <ToggleButton
                  visible={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword((v) => !v)}
                />
              </div>
              {errors.confirm_password && (
                <p className="text-xs text-error">{errors.confirm_password}</p>
              )}
            </div>

            {/* Organization Name (optional) */}
            <div className="space-y-1.5">
              <Label htmlFor="org_name">Organization Name</Label>
              <Input
                id="org_name"
                type="text"
                placeholder="Acme Corp (optional)"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                autoComplete="organization"
                disabled={loading}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="invite_code">Invite Code</Label>
              <Input
                id="invite_code"
                type="text"
                placeholder="FTL-XXXX-XXXX (if you have one)"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                autoComplete="off"
                disabled={loading}
              />
            </div>

            {/* Legal acceptance — required for GDPR/CCPA compliance */}
            <div className="space-y-1.5">
              <label htmlFor="accept_terms" className="flex items-start gap-2 cursor-pointer">
                <input
                  id="accept_terms"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={loading}
                  className="mt-0.5"
                />
                <span className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  I agree to the{" "}
                  {/* WCAG: text-brand-500 on light bg fails 4.5:1
                      contrast; text-brand-700 hits 5.5:1. underline
                      is required (1.4.1) so links are distinguishable
                      without colour. */}
                  <Link to="/legal/terms" target="_blank" className="text-brand-700 dark:text-brand-300 underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/legal/privacy" target="_blank" className="text-brand-700 dark:text-brand-300 underline">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
              {errors.terms && (
                <p className="text-xs text-error">{errors.terms}</p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={loading}
            >
              {loading ? "Creating account..." : "Create account"}
            </Button>
          </form>

          {/* Social sign-up buttons — only shown when at least one provider is configured */}
          {socialProviders && (socialProviders.google.enabled || socialProviders.apple.enabled) && (
            <>
              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200 dark:border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-slate-900 px-2 text-slate-500 dark:text-slate-400">Or sign up with</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {socialProviders.google.enabled && (
                  <button
                    type="button"
                    onClick={() => handleSocialSignup("google")}
                    disabled={!!socialLoading || loading}
                    className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
                  >
                    {socialLoading === "google" ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <GoogleIcon className="h-5 w-5" />
                    )}
                    Sign up with Google
                  </button>
                )}
                {socialProviders.apple.enabled && (
                  <button
                    type="button"
                    onClick={() => handleSocialSignup("apple")}
                    disabled={!!socialLoading || loading}
                    className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-black px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-900 disabled:opacity-50"
                  >
                    {socialLoading === "apple" ? (
                      <Loader2 className="h-5 w-5 animate-spin text-white" />
                    ) : (
                      <AppleIcon className="h-5 w-5" />
                    )}
                    Sign up with Apple
                  </button>
                )}
              </div>
            </>
          )}
    </AuthShell>
  );
}

export default Register;
